export interface LinkGroupLink {
  label: string;
  href: string;
}

export interface LinkGroup {
  heading: string;
  links: LinkGroupLink[];
}

/**
 * A `link_groups` metafield persists as a JSON-encoded string —
 * `[{ "heading": "...", "links": [{ "label": "...", "href": "..." }] }]` —
 * the same value the website's browse-related section parses. The backend
 * treats it as opaque text, like the experts-smart-filter query string.
 */
export function parseLinkGroupsValue(value: unknown): LinkGroup[] {
  if (typeof value !== 'string' || !value.trim()) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  return parsed.map((group) => {
    const record = (group && typeof group === 'object' ? group : {}) as Record<string, unknown>;
    const links = Array.isArray(record.links) ? record.links : [];
    return {
      heading: typeof record.heading === 'string' ? record.heading : '',
      links: links.map((link) => {
        const linkRecord = (link && typeof link === 'object' ? link : {}) as Record<string, unknown>;
        return {
          label: typeof linkRecord.label === 'string' ? linkRecord.label : '',
          href: typeof linkRecord.href === 'string' ? linkRecord.href : '',
        };
      }),
    };
  });
}

/** Empty string (not "[]") when nothing remains, so the page metafield reads as unset. */
export function serializeLinkGroupsValue(groups: LinkGroup[]): string {
  const cleaned = groups
    .map((group) => ({
      heading: group.heading.trim(),
      links: group.links
        .map((link) => ({ label: link.label.trim(), href: link.href.trim() }))
        .filter((link) => link.label || link.href),
    }))
    .filter((group) => group.heading || group.links.length);

  return cleaned.length ? JSON.stringify(cleaned) : '';
}

export function countLinkGroupLinks(groups: LinkGroup[]): number {
  return groups.reduce((total, group) => total + group.links.filter((link) => link.label || link.href).length, 0);
}

export function createEmptyLinkGroup(): LinkGroup {
  return { heading: '', links: [createEmptyLink()] };
}

export function createEmptyLink(): LinkGroupLink {
  return { label: '', href: '' };
}
